import axiosInstanceApiGateway from 'config/apiServiceApiGateway'
import { Get, Post } from 'config/apiServiceConfig'
import type { HalalInfoType } from 'features/Order/utils/types'
import { OrderLineItemsType } from '../redux/PopupCheckQTY/slice'
import { AstroApprovalPinAuthenticationPayloadType } from './order'

interface OrderDriverType {
  driver_id: number
  driver_full_name: string
  driver_plate_number: Nullable<string>
  driver_phone_number: Nullable<string>
}

interface OrderPackerType {
  driver_id: number
  driver_full_name: string
  driver_plate_number: Nullable<string>
  driver_phone_number: Nullable<string>
}

interface OrderPickerType {
  driver_id: number
  driver_full_name: string
  driver_plate_number: Nullable<string>
  driver_phone_number: Nullable<string>
}

export interface OrderTypeResponseInterface {
  order_line_items_gift: OrderLineItemsType[]
  fulfill_status: string
  gift_note: string
  is_new_customer: boolean
  is_picking_from_app_enable: Nullable<boolean>
  order_created_at: number
  order_customer_address: string
  order_driver: Nullable<OrderDriverType>
  order_id: number
  order_invoice: string
  order_kitchen_status: 'COMPLETED' | 'IN PROGRESS' | 'CANCELLED'
  order_line_items: OrderLineItemsType[]
  order_line_items_consumable: (OrderLineItemsType & { recommendation_quantity: number })[]
  order_line_items_extra_consumable: OrderLineItemsType[]
  order_location_id: number
  order_location_quadrant: number
  order_maximum_duration: number
  order_note: string
  order_packer: Nullable<OrderPackerType>
  order_payment_status: string
  order_payment_verified_at: number // Unix timestamp
  order_picked_at: number // Unix timestamp
  order_picker: Nullable<OrderPickerType>
  order_recipient_name: string
  order_sender_name: string
  order_service_level_name: string
  order_status:
    | 'New Order'
    | 'Picking'
    | 'Packing'
    | 'Waiting Driver'
    | 'Ready For Delivery'
    | 'Back To Hub'
    | 'Completed'
    | 'Cancelled'
    | 'In Review'
  order_type: 'INSTANT' | 'SUPER'
  report_timestamp: number
  total_relabeled_item: number
}

export type PackingItemType = {
  id: number
  monolith_id: string
  type: string
  kitchen_item_id: number
  special_type: string
  product: {
    id: string
    name: string
    sku: string
    brand_name: string
    image_url: string
    bundle_product_id: string
    has_kitchen: boolean
  }
  inventory: {
    rack: string
    expiry_date?: string
    order_qty: number
    scanned_qty: number
    note_id?: string
    note?: string
    status_id?: number
  }
  details: Array<{
    id: string
    qty: number
  }>
  modifiers: ModifierType[]
  add_ons: ModifierType[]
  halal_info?: HalalInfoType
  misc: {
    use_expiry_date?: boolean
    is_manual_input_sku: boolean
    is_need_to_relabel: boolean
  }
}

export type ModifierType = {
  id: string
  name: string
  variants: Array<{
    id: string
    name: string
    modifier_type: string
    change_type: string
    price_change: number
  }>
}

export type ConsumableItemType = {
  id: number
  product: {
    id: string
    name: string
    sku: string
  }
  inventory: {
    rack: string
    scanned_qty: number
    recommend_qty: number
  }
}

export type PackingDetailResponseType = {
  order: {
    order_id: string
    type: string
    invoice: string
    status: string
    kitchen_status: string
  }
  timestamp: {
    payment_verified_at: number
    waiting_confirmation_at: number
  }
  items: PackingItemType[]
  consumables: ConsumableItemType[]
  misc: {
    total_relabeled_item: number
    note: string
    gift: {
      sender_name: string
      receiver_name: string
      note: string
    }
  }
}

export type DeleteScannedProductPayloadType = {
  product_id: string
  type: string
  fulfillment_id: string
}

export const getPackingDetailByInvoice = (params: { invoice: string }) =>
  Get<{ data: OrderTypeResponseInterface }>({ url: '/api/packer/order/detail', params })

export const getPackingDetailByFulfillment = (fulfillment_id: string) =>
  axiosInstanceApiGateway.Get<PackingDetailResponseType, true>({
    url: `/fulfillment/internal/v1/admin/fulfillments/${fulfillment_id}/packing`,
  })

export const putDeleteScannedProduct = (payload: DeleteScannedProductPayloadType) =>
  axiosInstanceApiGateway.Put({
    url: `fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/packing/reset-scan`,
    data: payload,
  })

export const getKitchenItemByItemID = (payload: {
  fulfillment_id: string
  item_id: number
  quantity: number
}) =>
  axiosInstanceApiGateway.Post({
    url: `/fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/packing/scan-kitchen`,
    data: payload,
  })

// export const getProductDetailExpiredSearchQrCode = ({ qr_code, quantity, orderID, type }) =>
//   axios.put(`${baseUrl}/api/packer/order/${orderID}/scan-product`, {
//     qr_code,
//     quantity,
//     product_type: type ? 3 : null,
//   })

export type ScanProductPayloadType = {
  fulfillment_id: string
  sku: string
  quantity: number
}
export type ScanKitchenPayloadType = {
  fulfillment_id: string
  item_id: string
  quantity: number
}

export type FinishPackingPayloadType = {
  fulfillment_id: string
  partial_fulfill_items: {
    item_id: number
    partial_note: {
      note_id?: string
      status_id?: number
      display_name?: string
    }
  }[]
}

export type FinishPackingResponseType = {
  validation?: {
    code: number
    message: string
  }
}

export type GetReceiptByFulfillmentIDResponseType = {
  fulfillment_id: string
  order: {
    order_id: string
    type: string
    invoice: string
    note: string
    created_at: string
  }
  external_order: {
    external_order_id: string
    external_second_id: string
  }
  product_handling: {
    is_twenty_one_plus: boolean
  }
  packages: {
    id: number
    package_id: string
    confirmed_time: string
    package_label: string
    staging_area: string
  }[]
  timing: {
    type: string
    id: string
    min_arrive_at: string
    max_arrive_at: string
  }
  receiver: {
    customer_id: number
    name: string
    address_label: string
    address_place: string
    customer_address_id: number
    address: string
    address_description: string
    phone: string
    note: string
  }
  delivery_handling: {
    is_eggs: boolean
    is_rotten: boolean
    is_fragile: boolean
    is_melting: boolean
    has_big_item: boolean
    is_refill: boolean
  }
  misc: {
    twenty_one_plus_category: boolean
    location_quadrant?: number
  }
}

export type GetReceiptPayloadType = {
  fulfillment_id: string
}

export type PostReceiptBypassPayloadType = AstroApprovalPinAuthenticationPayloadType & {
  action: string
  reason: string
  fulfillment_id: string
  other_reason_description?: string
}

export type ConfirmPrintingPayloadType = AstroApprovalPinAuthenticationPayloadType & {
  fulfillment_id: number
  reason: string
}

export type GetReceiptByOrderIDResponseType = {
  order: {
    created_at: number
    id: number
    invoice: string
    note: string
    type: string
  }
  receiver: {
    address_description: string
    address_detail: string
    address_id: number
    address_instruction: string
    address_label: string
    address_place: string
    address_quadrant: number
    customer_id: number
    customer_name: string
    customer_phone_number: string
  }
  packages: {
    bypassed_confirmation: boolean
    confirmed_time: number
    id: string
    label: string
    staging_area: string
  }[]
  misc: {
    booking_id: string
    twenty_one_plus_category: boolean
  }
  delivery_handling: {
    is_eggs: boolean
    is_rotten: boolean
    is_fragile: boolean
    is_melting: boolean
    has_big_item: boolean
    is_refill: boolean
  }
}

export const getProductEDBySKU = (payload: ScanProductPayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/packing/scan-item`,
    data: payload,
  })

export const getProductKitchenByID = (payload: ScanKitchenPayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/packing/scan-kitchen`,
    data: payload,
  })

export const getProductPackagingBySKU = (payload: ScanProductPayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/packing/scan-packaging`,
    data: payload,
  })

export const putFinishPacking = (payload: FinishPackingPayloadType) =>
  axiosInstanceApiGateway.Post<FinishPackingResponseType, true>({
    url: `fulfillment/internal/v1/admin/fulfillments/${payload.fulfillment_id}/packing/finish`,
    data: payload,
  })

export type RemovableItemResponseType = {
  product_id: string | number
  product_name: string
  product_image: string
  item_id: string | number
  type: string
  rack_name: string
  quantity: string | number
  sku: string
  halal_info?: HalalInfoType
}

export const getRemovableItems = (fulfillmentID: string) =>
  axiosInstanceApiGateway.Get<{ removable_items?: RemovableItemResponseType[] }>({
    url: `/fulfillment/internal/v1/admin/fulfillments/${fulfillmentID}/removable-items`,
  })

export const postScanToRemoveItem = (orderID: number, payload: ScanProductPayloadType) =>
  Post({
    url: `/api/packer/order/${orderID}/scan-product/remove`,
    data: payload,
  })

export const notifyPartialFulfill = (orderID: number) =>
  Post({
    url: `/api/packer/order/${orderID}/notify-partial-fulfill`,
  })

export const getReceiptByFulfillmentID = (fulfillment_id: string) =>
  axiosInstanceApiGateway.Get<GetReceiptByFulfillmentIDResponseType, true>({
    url: `/fulfillment/internal/v1/printing/fulfillments/${fulfillment_id}/receipt`,
  })

export const getReceiptByOrderID = (order_id: string) =>
  axiosInstanceApiGateway.Get<GetReceiptByOrderIDResponseType>({
    url: `/fulfillment/internal/v1/printing/orders/${order_id}/receipt`,
  })

export const postBypassReceiptValidation = (payload: PostReceiptBypassPayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `/fulfillment/internal/v1/printing/fulfillments/${payload.fulfillment_id}/bypass-receipt-validation`,
    data: payload,
  })

export const postConfirmPrinting = (payload: ConfirmPrintingPayloadType) =>
  axiosInstanceApiGateway.Post({
    url: `/fulfillment/internal/v1/printing/admin/fulfillments/${payload.fulfillment_id}/confirm`,
    data: payload,
  })

export const getReasonsForBypassReceipt = (action: string) =>
  axiosInstanceApiGateway.Get<{ reasons: { id: number; description: string }[] }>({
    url: `/fulfillment/internal/v1/admin/action-reasons?action=${action}`,
  })
